/*
 *   File : manage-risk-score.js
 *   Author : https://evoqins.com
 *   Description : manage risk score component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setPageTitle, setSearchEnabled } from "../../Redux/Ui/ui.slice";
import { Tabs } from "../../Components/Navigation";
import { PrimaryButton } from "../../Components/Buttons";
import { DataTableComponent } from "../../Components/DataTable";
import { useLocation, useNavigate } from "react-router-dom";
import EditRiskScore from "./edit-risk-score";
import { DeleteModal, RiskModal } from "../../Components/Modals";
import SuccessModal from "../../Components/Modals/success-modal";
import { APIService } from "../../Service";
import { EmptyScreen, Loader } from "../../Components/Other";

const TAB_DATA = [
  {
    id: 0,
    label: "assesmentQuestions",
    name: "Assesment Questions",
  },
  {
    id: 1,
    label: "riskProfileScore",
    name: "Risk Profile Score",
  },
];

const RISK_COLUMNS = [
  {
    name: "Risk Types",
    selector: (row) => row.riskType,

    width: "380px",
    cell: (row) => {
      const risk = row.riskType?.toLowerCase();

      let colorClass = "";
      switch (risk) {
        case "very low":
          colorClass = "text-vivid-green"; // green
          break;
        case "low":
          colorClass = "text-lime-green"; // lighter green
          break;
        case "moderate":
          colorClass = "text-honey"; // blue
          break;
        case "high":
          colorClass = "text-vivid-orange"; // red tone
          break;
        case "very high":
          colorClass = "text-pure-red"; // darker red
          break;
        default:
          colorClass = "text-eerie-black";
      }

      return <p className={`${colorClass} mb-0`}>{row.riskType}</p>;
    },
  },

  {
    name: "Score Range",
    selector: (row) => row.scoreRange,
  },
];

const RISK_DATA = [
  {
    id: 1,
    riskType: "Very Low",
    scoreRange: "0 - 20",
  },
  {
    id: 2,
    riskType: "Low",
    scoreRange: "21 - 40",
  },
  {
    id: 3,
    riskType: "Moderate",
    scoreRange: "41 - 60",
  },
  {
    id: 4,
    riskType: "High",
    scoreRange: "61 - 80",
  },
  {
    id: 5,
    riskType: "Very High",
    scoreRange: "81 - 100",
  },
];

export default function ManageRiskScore() {
  const navigate = useNavigate();
  const [curTab, setCurTab] = useState(0);
  const [editScore, setEditScore] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [riskScores, setRiskScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewDeleteOpen, setViewDeleteOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [successModal, setSuccessModal] = useState(false);
  const location = useLocation();

  //Redux title and searchbar setting
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setPageTitle(["Manage Risk Score"]));
    dispatch(setSearchEnabled(false));
  }, []);

  useEffect(() => {
    _fetchRiskQuestions();
    _fetchRiskScores();
  }, []);

  useEffect(() => {
    // URL → state sync
    if (location.pathname === "/manage-risk-score/risk/edit") {
      setEditScore(true);
    } else {
      dispatch(setPageTitle(["Manage Risk Score"]));
      setEditScore(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.includes("/risk")) {
      setCurTab(1);
    } else {
      setCurTab(0);
    }
  }, [location.pathname]);

  // service to fetch questions
  const _fetchRiskQuestions = async () => {
    setLoading(true);
    try {
      const payload = {
        page_num: 1,
        page_size: 12,
      };
      const res = await APIService(true, "risk/question-list", payload);

      if (res.status_code === 200 && res.data?.data) {
        const mapped = res.data.data.map((q) => ({
          id: q.question_id,
          question: q.question,
          type: q.question_type,
          weight: q.weight,
          answers: q.answers,
          order: q.order,
        }));
        
        // Calculate total weightage
        const totalWeight = mapped.reduce(
          (sum, row) => sum + Number(row.weight || 0),
          0
        );
        
        // Add total row to the data
        setQuestions([
          ...mapped,
          {
            id: "total-row",
            question: "TOTAL WEIGHTAGE",
            type: "",
            weight: totalWeight,
            answers: [],
            order: null,
          },
        ]);
        setError(false);
      } else {
        setQuestions([]);
        setError(true);
      }
    } catch (err) {
      console.error("risk/question-list error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // service to fetch  risk scores
  const _fetchRiskScores = async () => {
    try {
      const res = await APIService(false, "risk/list-score");

      if (res.status_code === 200 && Array.isArray(res.data)) {
        const mapped = res.data.map((r) => ({
          id: r.id,
          riskType: r.name,
          scoreRange: `${r.score_from} - ${r.score_to}`,
        }));
        setRiskScores(mapped);
      } else {
        setRiskScores([]);
      }
    } catch (err) {
      console.error("risk/list-score error:", err);
    }
  };

  // service to delete question
  const _deleteQuestion = async () => {
    if (!selectedQuestion?.id) return;

    try {
      const payload = {
        question_id: String(selectedQuestion.id),
      };

      const res = await APIService(true, "risk/delete-question", payload);

      if (res.status_code === 200) {
        setViewDeleteOpen(false);
        setSuccessModal(true);

        // refresh list
        _fetchRiskQuestions();
      } else {
        console.error("Delete failed:", res);
      }
    } catch (error) {
      console.error("risk/delete-question error:", error);
    }
  };

   const ASSESSMENT_COLUMNS = [
    {
      name: "Question",
      selector: (row) => row.question,

      wrap: true,
      width: "450px",
      cell: (row) => (
        <p className={`mb-0 ${row.id === "total-row" ? "fw-600" : ""}`}>
          {row.question}
        </p>
      ),
    },

    {
      name: "Type",
      selector: (row) => row.type,

      width: "180px",
    },

    {
      name: "Weightage",
      selector: (row) => row.weight,

      width: "180px",
      cell: (row) => (
        <p className={`mb-0 ${row.id === "total-row" ? "fw-600" : ""}`}>
          {row.weight}
        </p>
      ),
    },

    {
      name: "Actions",
      width: "340px",
      cell: (row) => {
        // Hide actions for total row
        if (row.id === "total-row") {
          return null;
        }
        
        return (
          <div className="d-flex align-items-center col-g-42">
            {/* VIEW */}
            <PrimaryButton
              ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
              name="View"
              onPress={() => {
                console.log(row);

                setSelectedQuestion(row);
                setViewModalOpen(true);
              }}
            />

            {/* EDIT */}
            <PrimaryButton
              ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
              name="Edit"
              onPress={() => navigate(`/manage-risk-score/edit/${row.id}`)}
            />

            {/* DELETE */}
            <PrimaryButton
              ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
              name="Delete"
              onPress={() => {
                setSelectedQuestion(row);
                setViewDeleteOpen(true);
              }}
            />
          </div>
        );
      },
      ignoreRowClick: true,
    },
  ];

  if (loading) return <Loader />;
  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column pt-24">
          <div className="e-glass-card bg-transparent overflow-hidden d-flex flex-column rounded-32  row-gap-3">
            {editScore ? (
              <div className="row">
                <div className="col-lg-9 col-12">
                  <div className="d-flex flex-column p-24 ">
                    <EditRiskScore tabChange={() => setCurTab(1)} />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between p-24 ">
                  <Tabs
                    data={TAB_DATA}
                    defaultActive={curTab}
                    onChange={(activeId) => setCurTab(activeId)}
                  />
                  <PrimaryButton
                    name={curTab === 0 ? "Add" : "Edit"}
                    ClassName={
                      "bg-oxford-blue fw-400 fs-16 lh-24 text-white rounded-16 py-2 px-3"
                    }
                    onPress={() => {
                      curTab === 0
                        ? navigate("/manage-risk-score/add")
                        : navigate("/manage-risk-score/risk/edit");
                    }}
                  />
                </div>
                {error ? (
                  <EmptyScreen message={"Failed to load risk data"} />
                ) : (
                  <div className="d-flex">
                    <DataTableComponent
                      info={false}
                      columns={curTab === 0 ? ASSESSMENT_COLUMNS : RISK_COLUMNS}
                      data={curTab === 0 ? questions : riskScores}
                      pagination={false}
                      className={"e-table-basic"}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <RiskModal
        open={viewModalOpen}
        data={selectedQuestion}
        onClose={() => setViewModalOpen(false)}
      />
      <DeleteModal
        message={"Are you sure you want to delete?"}
        open={viewDeleteOpen}
        title="View Question"
        onCancel={() => setViewDeleteOpen(false)}
        onConfirm={_deleteQuestion}
      />

      <SuccessModal
        title={"Deletion Successful"}
        message={
          "Item has been successfully removed from the system. This action is permanent."
        }
        open={successModal}
        onConfirm={() => setSuccessModal(false)}
      />
    </div>
  );
}